import type { IBaseControlSetting } from '~/presentation/reports/common/type';
import type { SelectOptionType, SelectValueType } from '../../type';

import { EControlType, EFilterField } from '~/presentation/reports/common/type';
import { mapPageToOption, isOperatorConfig, getValueArray } from '../utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SelectCompound } from '../../SelectCompound';
import { usePageAnalyticsMetadata } from '../hooks';

export interface IControlSelectPage extends IBaseControlSetting {
  type: EControlType.SELECT_PAGE;
}

type CachedOptionsMap = Record<string, SelectOptionType>;

export const SelectPage: React.FC<IControlSelectPage> = (props) => {
  const [isOpenPageModal, setIsOpenPageModal] = useState(false);
  const [cachedSelectedOptions, setCachedSelectedOptions] = useState<CachedOptionsMap>({});

  const { data: pageAnalyticsData, isLoading } = usePageAnalyticsMetadata({
    enabled: isOpenPageModal,
  });

  const handlePopoverOpenChange = useCallback((isOpen: boolean) => {
    setIsOpenPageModal(isOpen);
  }, []);

  const pageOptions = useMemo(() => {
    if (!pageAnalyticsData?.length) return undefined;

    return pageAnalyticsData.map(mapPageToOption);
  }, [pageAnalyticsData]);

  const selectedValues = useMemo(() => {
    const pageConfigs = props.configs?.filter(
      (config) => config.id === EFilterField.SINGLE_PAGE || config.id === EFilterField.LIST_PAGE,
    );
    return pageConfigs?.flatMap((config) => config.defaultValue);
  }, [props.configs]);

  const getCachedOptionsForValues = useCallback(
    (value?: SelectValueType): SelectOptionType[] => {
      if (!value) return [];

      const valueArray = getValueArray(value);
      return valueArray
        .map((value) => cachedSelectedOptions[value])
        .filter((option): option is SelectOptionType => !!option);
    },
    [cachedSelectedOptions],
  );

  const getOptionsForConfig = useCallback(
    (defaultValue?: SelectValueType): SelectOptionType[] | undefined => {
      if (!defaultValue) return pageOptions;

      const defaultValues = getValueArray(defaultValue);

      const isDefaultValueInOptions = defaultValues.every((value) =>
        pageOptions?.some((option) => option.value === value),
      );

      if (isDefaultValueInOptions) {
        return pageOptions;
      }

      const cachedOptions = getCachedOptionsForValues(defaultValue);
      // If the searched value is not found in cached options, fallback to pageOptions
      return cachedOptions.length ? cachedOptions : pageOptions;
    },
    [pageOptions, getCachedOptionsForValues],
  );

  const enhancedConfigs = useMemo(() => {
    return props.configs?.map((config) => {
      if (isOperatorConfig(config.id)) {
        return config;
      }

      return {
        ...config,
        isLoading: isLoading && isOpenPageModal,
        width: '248px',
        onOpenPopover: handlePopoverOpenChange,
        options: getOptionsForConfig(config.defaultValue),
      };
    });
  }, [props.configs, isLoading, isOpenPageModal, handlePopoverOpenChange, getOptionsForConfig]);

  useEffect(() => {
    if (!selectedValues?.length || !pageAnalyticsData?.length) return;

    setCachedSelectedOptions((prevCached) => {
      const updatedCache: CachedOptionsMap = { ...prevCached };

      for (const value of selectedValues) {
        if (!value) continue;

        const pageData = pageAnalyticsData.find((page) => page.locationPath === value);
        if (!pageData) continue;

        updatedCache[value] = mapPageToOption(pageData);
      }

      return updatedCache;
    });
  }, [selectedValues, pageAnalyticsData]);

  return (
    <SelectCompound {...props} configs={enhancedConfigs} type={EControlType.SELECT_COMPOUND} />
  );
};
